// deco-waves.js — brings the two decorative sweeps in the Helix section to life.
//
// The SVG paths in the markup stay the source of truth: on boot each one is
// sampled into a polyline, and every frame the samples are pushed along their
// own normals by a couple of travelling sine waves. The result is redrawn as a
// smooth Catmull-Rom curve, so the lines keep their original sweep while
// rippling like a slack cable. Scroll velocity feeds a decaying gain so the
// ripple swells when the page moves and settles again when it stops.
//
// Purely CSS/SVG — this layer never touches WebGL.

const SAMPLES = 132;          // points sampled along each base path
const TAU = Math.PI * 2;

// per-line character: amplitude (viewBox units), wavelength, speed, direction
const LINES = [
  { amp: 22, waves: 1.6, speed: 0.34, phase: 0.0, drift: -0.8, gain: 1.0 },
  { amp: 15, waves: 2.3, speed: -0.27, phase: 1.7, drift: 0.6, gain: 0.7 },
];

export function initDecoWaves() {
  const svg = document.querySelector('.helixArea .deco');
  if (!svg) return;

  const paths = [...svg.querySelectorAll('path')];
  if (!paths.length) return;

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // ── sample each base path once ────────────────────────────────────────
  const lines = paths.map((path, i) => {
    const cfg = LINES[i % LINES.length];
    const len = path.getTotalLength();
    const pts = [];
    for (let s = 0; s <= SAMPLES; s++) {
      const u = s / SAMPLES;
      const p = path.getPointAtLength(u * len);
      pts.push({ x: p.x, y: p.y, u });
    }
    // normals from the neighbouring samples (tangent rotated a quarter turn)
    const nrm = pts.map((_, s) => {
      const a = pts[Math.max(s - 1, 0)];
      const b = pts[Math.min(s + 1, pts.length - 1)];
      const dx = b.x - a.x, dy = b.y - a.y;
      const m = Math.hypot(dx, dy) || 1;
      return { x: -dy / m, y: dx / m };
    });
    return { path, pts, nrm, cfg, out: new Array(pts.length) };
  });

  // ── scroll velocity → extra swell, decaying back to rest ──────────────
  let lastY = window.scrollY;
  let boost = 0;
  const onScroll = () => {
    const y = window.scrollY;
    boost = Math.min(boost + Math.abs(y - lastY) * 0.012, 1.6);
    lastY = y;
  };
  addEventListener('scroll', onScroll, { passive: true });

  // ── only run while the section is actually on screen ──────────────────
  let visible = true;
  let raf = 0;
  const io = new IntersectionObserver(([e]) => {
    visible = e.isIntersecting;
    visible ? play() : stop();
  }, { rootMargin: '10% 0px' });
  io.observe(svg.closest('.helixArea') || svg);

  let t0 = performance.now();
  function frame(now) {
    const t = (now - t0) / 1000;
    boost *= 0.94;                       // ease the scroll swell away

    for (const line of lines) {
      const { pts, nrm, cfg, out } = line;
      const amp = cfg.amp * (1 + boost * cfg.gain);
      for (let s = 0; s < pts.length; s++) {
        const p = pts[s];
        // two detuned harmonics keep the ripple from looking metronomic
        const w = Math.sin(p.u * TAU * cfg.waves - t * cfg.speed * TAU + cfg.phase)
                + Math.sin(p.u * TAU * cfg.waves * 2.3 + t * cfg.speed * TAU * 0.6 + cfg.phase * 2) * 0.32;
        // soft envelope: the sweep breathes most through its middle
        const env = 0.55 + 0.45 * Math.sin(p.u * Math.PI);
        const d = w * amp * env + Math.sin(t * 0.21 + cfg.phase) * cfg.drift * 4;
        out[s] = { x: p.x + nrm[s].x * d, y: p.y + nrm[s].y * d };
      }
      line.path.setAttribute('d', toCurve(out));
    }

    raf = requestAnimationFrame(frame);
  }

  function play() {
    if (raf || !visible || document.hidden) return;
    t0 = performance.now() - 1;
    raf = requestAnimationFrame(frame);
  }
  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  }

  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
  play();

  addEventListener('pagehide', () => {
    stop();
    io.disconnect();
    removeEventListener('scroll', onScroll);
  }, { once: true });
}

// Catmull-Rom through the samples, emitted as cubic beziers — silky at any size.
function toCurve(p) {
  let d = `M${p[0].x.toFixed(2)} ${p[0].y.toFixed(2)}`;
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[Math.max(i - 1, 0)];
    const p1 = p[i];
    const p2 = p[i + 1];
    const p3 = p[Math.min(i + 2, p.length - 1)];
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d += `C${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}
